import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faChartLine,
  faCalendarDay,
  faArrowTrendUp,
  faComments,
  faVideo,
  faBook,
  faGraduationCap,
  faClock,
  faRocket,
  faBolt,
  faFilter,
  faMagnifyingGlassPlay
} from '@/fontawesome';
import { formatCurrencyCentValue } from '@/utils/helpers';
import type { FeatureConfig } from '@/models';

interface FeatureIconsProps {
  featureConfig: FeatureConfig;
}

export default function FeatureIcons({ featureConfig }: FeatureIconsProps) {
  const getFeatureStatus = (enabled: boolean, limit: number | null) => {
    if (!enabled) {
      return {
        bgColor: 'bg-gray-200 dark:bg-gray-700',
        iconColor: 'text-gray-400 dark:text-gray-600',
        opacity: 'opacity-40'
      };
    }
    if (limit !== null) {
      return {
        bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
        iconColor: 'text-yellow-600 dark:text-yellow-400',
        opacity: 'opacity-100'
      };
    }
    return {
      bgColor: 'bg-green-100 dark:bg-green-900/30',
      iconColor: 'text-green-600 dark:text-green-400',
      opacity: 'opacity-100'
    };
  };

  const getCourseStatus = () => {
    if (featureConfig.allCourses) {
      return {
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        iconColor: 'text-green-600 dark:text-green-400',
        opacity: 'opacity-100',
        label: 'Alle Kurse'
      };
    }
    const enabledCount = Object.values(featureConfig.courses).filter(v => v).length;
    if (enabledCount === 0) {
      return {
        bgColor: 'bg-gray-200 dark:bg-gray-700',
        iconColor: 'text-gray-400 dark:text-gray-600',
        opacity: 'opacity-40',
        label: 'Keine Kurse'
      };
    }
    return {
      bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
      iconColor: 'text-yellow-600 dark:text-yellow-400',
      opacity: 'opacity-100',
      label: `${enabledCount} Kurs${enabledCount > 1 ? 'e' : ''}`
    };
  };

  const coreStatus = getFeatureStatus(featureConfig.coreRecommendations, featureConfig.coreRecommendationStrikeLimit);
  const earningsStatus = getFeatureStatus(featureConfig.earningRecommendations, featureConfig.earningRecommendationStrikeLimit);
  const swingStatus = getFeatureStatus(featureConfig.swingTradeRecommendations, featureConfig.swingTradeRecommendationPriceLimit);
  const longTermStatus = getFeatureStatus(featureConfig.longTermRecommendations, featureConfig.longTermRecommendationPriceLimit);
  const boostStatus = getFeatureStatus(featureConfig.boostRecommendations, null);
  const courseStatus = getCourseStatus();

  const FeatureIcon = ({
    icon,
    status,
    tooltip
  }: {
    icon: IconProp;
    status: { bgColor: string; iconColor: string; opacity: string };
    tooltip: string;
  }) => (
    <div className="relative group">
      <div className={`w-7 h-7 rounded flex items-center justify-center ${status.bgColor} ${status.opacity}`}>
        <FontAwesomeIcon
          icon={icon}
          className={`w-3.5 h-3.5 ${status.iconColor}`}
        />
      </div>
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-10">
        {tooltip}
      </div>
    </div>
  );

  return (
    <div className="flex items-center space-x-2">
      <FeatureIcon
        icon={faChartLine}
        status={coreStatus}
        tooltip={
          featureConfig.coreRecommendations
            ? featureConfig.coreRecommendationStrikeLimit !== null
              ? `Core: Aktiv (Limit: ${formatCurrencyCentValue(featureConfig.coreRecommendationStrikeLimit, 'USD')})`
              : 'Core: Aktiv'
            : 'Core: Deaktiviert'
        }
      />

      <FeatureIcon
        icon={faCalendarDay}
        status={earningsStatus}
        tooltip={
          featureConfig.earningRecommendations
            ? featureConfig.earningRecommendationStrikeLimit !== null
              ? `Earnings: Aktiv (Limit: ${formatCurrencyCentValue(featureConfig.earningRecommendationStrikeLimit, 'USD')})`
              : 'Earnings: Aktiv'
            : 'Earnings: Deaktiviert'
        }
      />

      <FeatureIcon
        icon={faArrowTrendUp}
        status={swingStatus}
        tooltip={
          featureConfig.swingTradeRecommendations
            ? featureConfig.swingTradeRecommendationPriceLimit !== null
              ? `Swing: Aktiv (Limit: ${formatCurrencyCentValue(featureConfig.swingTradeRecommendationPriceLimit, 'USD')})`
              : 'Swing: Aktiv'
            : 'Swing: Deaktiviert'
        }
      />

      <FeatureIcon
        icon={faClock}
        status={longTermStatus}
        tooltip={
          featureConfig.longTermRecommendations
            ? featureConfig.longTermRecommendationPriceLimit !== null
              ? `Langzeit: Aktiv (Limit: ${formatCurrencyCentValue(featureConfig.longTermRecommendationPriceLimit, 'USD')})`
              : 'Langzeit: Aktiv'
            : 'Langzeit: Deaktiviert'
        }
      />

      <FeatureIcon
        icon={faRocket}
        status={boostStatus}
        tooltip={featureConfig.boostRecommendations ? 'Boost: Aktiv' : 'Boost: Deaktiviert'}
      />

      <FeatureIcon
        icon={faComments}
        status={getFeatureStatus(featureConfig.chatbot, null)}
        tooltip={featureConfig.chatbot ? 'Chatbot: Aktiv' : 'Chatbot: Deaktiviert'}
      />

      <FeatureIcon
        icon={faVideo}
        status={getFeatureStatus(featureConfig.videoChatbot, null)}
        tooltip={featureConfig.videoChatbot ? 'Video-Chatbot: Aktiv' : 'Video-Chatbot: Deaktiviert'}
      />

      <FeatureIcon
        icon={faBook}
        status={getFeatureStatus(featureConfig.journal, null)}
        tooltip={featureConfig.journal ? 'Journal: Aktiv' : 'Journal: Deaktiviert'}
      />

      <FeatureIcon
        icon={faBolt}
        status={getFeatureStatus(featureConfig.dayTrading, null)}
        tooltip={featureConfig.dayTrading ? 'Day Trading: Aktiv' : 'Day Trading: Deaktiviert'}
      />

      <FeatureIcon
        icon={faFilter}
        status={getFeatureStatus(featureConfig.optionScreener, null)}
        tooltip={featureConfig.optionScreener ? 'Options-Screener: Aktiv' : 'Options-Screener: Deaktiviert'}
      />

      <FeatureIcon
        icon={faMagnifyingGlassPlay}
        status={getFeatureStatus(featureConfig.videoSearch, null)}
        tooltip={featureConfig.videoSearch ? 'Video-Finder: Aktiv' : 'Video-Finder: Deaktiviert'}
      />

      <FeatureIcon
        icon={faGraduationCap}
        status={courseStatus}
        tooltip={courseStatus.label}
      />
    </div>
  );
}
